import { useState } from "react";
import { Plus, ClipboardList, Star, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi, fmtD } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, Empty, Card } from "@/components/kit";

const RES = [["pass", "Pass", "bg-emerald-600 text-white border-emerald-600"], ["fail", "Fail", "bg-rose-600 text-white border-rose-600"], ["na", "N/A", "bg-slate-700 text-white border-slate-700"]];
const tone = (p) => (p >= 80 ? "text-emerald-700" : p >= 65 ? "text-amber-700" : "text-rose-700");

const Stars = ({ n }) => <span className="inline-flex">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className={`h-4 w-4 ${i < n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}</span>;

function Result({ x, onBack }) {
  return (
    <div data-testid="inspection-result">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900"><ChevronLeft className="h-4 w-4" />All self-inspections</button>
      <Card className="mb-6">
        <div className="flex flex-wrap items-center gap-6">
          <div><div className="ka-eyebrow">Score</div><div data-testid="inspection-score" className={`font-display text-5xl font-extrabold ${tone(x.score)}`}>{x.score}%</div></div>
          <div><div className="ka-eyebrow">Estimated rating</div><div data-testid="inspection-rating" className="mt-1 flex items-center gap-2 font-display text-3xl font-extrabold">{x.estimated_rating}<Stars n={x.estimated_rating} /></div><div className="text-xs text-slate-500">A guide only, not an official FHRS score</div></div>
          <div className="ml-auto text-sm text-slate-600">{fmtD(x.date)} · {x.inspected_by}{x.actions_added > 0 && <div className="font-semibold text-emerald-700">{x.actions_added} fixes added to the action plan</div>}</div>
        </div>
        <div className="mt-6 grid gap-3 sm:grid-cols-3">{x.sections.map((s) => <div key={s.id} className="rounded-xl bg-slate-50 p-4"><div className="text-sm font-semibold">{s.title}</div><div className={`font-display text-2xl font-extrabold ${tone(s.pct)}`}>{s.pct}%</div><div className="text-xs text-slate-500">{s.passed} of {s.total} passed</div></div>)}</div>
        {x.summary && <p className="mt-4 text-sm text-slate-700">{x.summary}</p>}
      </Card>
      <h3 className="mb-3 text-lg font-bold">To fix</h3>
      <ul className="space-y-2">{x.answers.filter((a) => a.result === "fail").map((a) => <li key={a.id} className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm"><b>{a.text}</b>{a.note && <div className="text-rose-800">{a.note}</div>}</li>)}</ul>
      {!x.failed && <p className="text-sm text-slate-500">Nothing failed. Great work.</p>}
    </div>
  );
}

function Run({ venueId, onDone, onCancel }) {
  const { data: sections } = useApi("/self-inspections/questions");
  const [ans, setAns] = useState({});
  const [summary, setSummary] = useState("");
  const [addActions, setAddActions] = useState(true);
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  if (!sections) return null;
  const all = sections.flatMap((s) => s.questions);
  const answered = all.filter((q) => ans[q.id]?.result).length;
  const set = (id, p) => setAns({ ...ans, [id]: { ...ans[id], ...p } });
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/self-inspections", { venue_id: venueId, summary, add_actions: addActions, due_date: due || null, answers: all.map((q) => ({ id: q.id, result: ans[q.id]?.result, note: ans[q.id]?.note || "" })) });
      toast.success(`Self-inspection saved: ${data.score}%`);
      onDone(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="inspection-run" className="space-y-8">
      {sections.map((s) => (
        <div key={s.id}>
          <div className="ka-eyebrow mb-3">{s.title}</div>
          <div className="space-y-3">{s.questions.map((q) => (
            <div key={q.id} className={`ka-card p-4 ${ans[q.id]?.result === "fail" ? "border-rose-300 bg-rose-50/60" : ""}`}>
              <div className="mb-3 text-sm font-medium text-slate-800">{q.text}</div>
              <div className="flex gap-2">{RES.map(([v, l, on]) => <button key={v} type="button" data-testid={`insp-${q.id}-${v}`} onClick={() => set(q.id, { result: v })} className={`ka-press h-11 flex-1 rounded-xl border text-sm font-semibold ${ans[q.id]?.result === v ? on : "border-slate-300 bg-white text-slate-700"}`}>{l}</button>)}</div>
              {ans[q.id]?.result === "fail" && <input data-testid={`insp-${q.id}-note`} maxLength={300} placeholder="What's wrong?" className="ka-input mt-3" value={ans[q.id]?.note || ""} onChange={(e) => set(q.id, { note: e.target.value })} />}
            </div>
          ))}</div>
        </div>
      ))}
      <Field label="Overall notes"><textarea data-testid="insp-summary" rows={2} maxLength={1000} className="ka-input" value={summary} onChange={(e) => setSummary(e.target.value)} /></Field>
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <label className="flex items-center gap-2"><input data-testid="insp-add-actions" type="checkbox" checked={addActions} onChange={(e) => setAddActions(e.target.checked)} />Add failed items to the action plan</label>
        {addActions && <label className="flex items-center gap-2">Fix by <input data-testid="insp-due" type="date" className="ka-input w-44 py-1.5" value={due} onChange={(e) => setDue(e.target.value)} /></label>}
      </div>
      <div className="sticky bottom-20 flex gap-2 lg:bottom-4">
        <button onClick={onCancel} className="ka-press flex-1 rounded-full border border-slate-300 bg-white py-3 text-sm font-semibold">Cancel</button>
        <button data-testid="insp-submit" disabled={busy || answered < all.length} onClick={submit} className="ka-press flex-[2] rounded-full bg-emerald-600 py-3 text-sm font-semibold text-white disabled:opacity-60">{answered < all.length ? `${answered} of ${all.length} answered` : busy ? "Saving…" : "Finish inspection"}</button>
      </div>
    </div>
  );
}

export default function SelfInspections() {
  const { venueId, venue, can } = useAuth();
  const { data, reload } = useApi(venueId ? "/self-inspections" : null, { venue_id: venueId });
  const [mode, setMode] = useState(null);
  if (mode === "run") return <div><PageHeader eyebrow={venue?.name} title="New self-inspection" subtitle="Walk the kitchen like an EHO would. Mark each point pass, fail or N/A." /><Run venueId={venueId} onCancel={() => setMode(null)} onDone={(x) => { reload(); setMode(x); }} /></div>;
  if (mode) return <Result x={mode} onBack={() => setMode(null)} />;
  return (
    <div>
      <PageHeader eyebrow={venue?.name} title="Self-inspections" subtitle="A mock EHO inspection in 18 questions across the three areas inspectors score. Failed points become to-dos."
        actions={can("manage") && <button data-testid="start-inspection-button" onClick={() => setMode("run")} className="ka-press inline-flex items-center gap-1 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"><Plus className="h-4 w-4" />Start inspection</button>} />
      {data && !data.length && <Empty testid="inspections-empty" icon={ClipboardList} title="No self-inspections yet" text="Run one before your next EHO visit to see where you stand." />}
      <div className="space-y-3">{(data || []).map((x) => (
        <button key={x.id} data-testid={`inspection-row-${x.id}`} onClick={() => setMode(x)} className="ka-press ka-card flex w-full items-center gap-5 p-5 text-left hover:border-slate-400">
          <div className={`font-display text-3xl font-extrabold ${tone(x.score)}`}>{x.score}%</div>
          <div className="flex-1"><div className="font-semibold">{fmtD(x.date)} · {x.inspected_by}</div><div className="text-xs text-slate-500">{x.failed} to fix · {x.sections.map((s) => `${s.title} ${s.pct}%`).join(" · ")}</div></div>
          <Stars n={x.estimated_rating} />
        </button>
      ))}</div>
    </div>
  );
}
